import { useEffect, useState } from "react";
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import { Edit, Link as LinkIcon, Instagram, Twitter, Linkedin, Facebook, Github, Mail, Grid, Bookmark, Heart, MessageCircle, Bell, Sparkles, Zap, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import NavBar from "../components/NavBar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { type Profile, type Will } from "@/types/profile";
import { getTagColor } from "@/utils/tagColors";
import { useProfile, getFullProfileImageUrl } from "../contexts/ProfileContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar } from "@/components/ui/avatar";
import { format } from "date-fns";
import { authenticatedGet } from "../utils/apiUtils";
import { useAuth } from "../contexts/AuthContext";

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId: urlUserId } = useParams<{ userId: string }>();
  const { profile: contextProfile, loading: contextLoading, error: contextError, fetchProfile } = useProfile();
  const [viewingProfile, setViewingProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  // State for active tab
  const [activeTab, setActiveTab] = useState("postedWills");

  // Determine if we're viewing the current user's profile or another user's profile
  const isViewingOwnProfile = !urlUserId;
  
  // Function to fetch another user's profile
  const fetchUserProfile = async (userId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authenticatedGet(`${import.meta.env.VITE_API_URL}/profiles/${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        
        // Handle both property names for profile image URL
        if (data.profileImageUrl) {
          data.profilePictureUrl = data.profileImageUrl;
          data.profileImageUrl = getFullProfileImageUrl(data.profileImageUrl);
          data.profilePictureUrl = data.profileImageUrl;
        } else if (data.profilePictureUrl) {
          data.profileImageUrl = data.profilePictureUrl;
          data.profilePictureUrl = getFullProfileImageUrl(data.profilePictureUrl);
          data.profileImageUrl = data.profilePictureUrl;
        }
        
        setViewingProfile(data);
      } else {
        if (response.status === 404) {
          setViewingProfile(null);
        } else {
          throw new Error('Failed to load profile');
        }
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  // Effect for authentication check
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast.error("Please sign in to view profiles");
      navigate('/signin', { 
        state: { 
          from: location.pathname,
          userId: urlUserId 
        } 
      });
    }
  }, [isAuthenticated, authLoading, navigate, location.pathname, urlUserId]);

  // Effect for fetching profile data
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      if (urlUserId) {
        fetchUserProfile(urlUserId);
      } else {
        fetchProfile().catch(err => {
          console.error('Error fetching profile:', err);
        });
      }
    }
  }, [urlUserId, fetchProfile, isAuthenticated, authLoading]);

  // Get the appropriate profile
  const profile = isViewingOwnProfile ? contextProfile : viewingProfile;
  const isLoadingProfile = isViewingOwnProfile ? contextLoading : loading;
  const profileError = isViewingOwnProfile ? contextError : error;

  // Get privacy settings from profile
  const privacySettings = profile?.privacySettings;
  
  // Handle profile navigation - don't navigate if already on this profile
  const handleProfileClick = (userId: string) => {
    if (userId === profile?.userId) return;
    navigate(`/profile/${userId}`);
  };
  
  // Check if the current user is the profile owner
  const isProfileOwner = () => {
    const userId = localStorage.getItem('userId');
    return userId === profile?.userId;
  };
  
  // Determine if profile should be visible
  const isProfileVisible = () => {
    // If it's the profile owner, always show
    if (isProfileOwner()) return true;
    
    if (!privacySettings) return true; // Default to visible if no settings
    
    // Check visibility based on privacySettings
    switch (privacySettings.profileVisibility) {
      case 'PUBLIC':
        return true;
      case 'PRIVATE':
        return false;
      case 'CONNECTIONS_ONLY':
        // For now, we don't have connections implemented, so default to private
        // TODO: Implement logic to check if user is a connection
        return false;
      default:
        return true;
    }
  };
  
  // Helper function to check if specific content should be visible
  const shouldShowContent = (contentType: 'reviews' | 'willHistory' | 'contactInfo') => {
    // For contact info, we should respect the showContactInfo setting even for the profile owner
    if (contentType === 'contactInfo') {
      // If profile has direct showContactInfo property, use that
      if (profile.showContactInfo !== undefined) {
        return profile.showContactInfo;
      }
      // Otherwise, fall back to privacySettings if available
      return privacySettings?.showContactInfo ?? false;
    }
    
    // For other content types:
    
    // If it's the profile owner, always show
    if (isProfileOwner()) return true;
    
    // If profile is not visible, don't show any content
    if (!isProfileVisible()) return false;
    
    // For other content types, check privacySettings
    if (!privacySettings) return false; // Default to not visible if no settings
    
    // Check specific content visibility
    switch (contentType) {
      case 'reviews':
        return privacySettings.showReviews;
      case 'willHistory':
        return privacySettings.showWillHistory;
      default:
        return false;
    }
  };

  // Get currency symbol helper - moved to component scope
  const getCurrencySymbol = (currencyCode: string | null | undefined): string => {
    if (!currencyCode) return '₪';
    
    const symbolMap: { [key: string]: string } = {
      'ILS': '₪',
      'USD': '$',
      'EUR': '€',
      'GBP': '£'
    };
    
    return symbolMap[currencyCode] || '₪';
  };

  // Function to render social media icons
  const renderSocialIcon = (platform: string) => {
    const platformLower = platform.toLowerCase();
    if (platformLower.includes('instagram')) return <Instagram className="h-5 w-5" />;
    if (platformLower.includes('twitter') || platformLower.includes('x.com')) return <Twitter className="h-5 w-5" />;
    if (platformLower.includes('linkedin')) return <Linkedin className="h-5 w-5" />;
    if (platformLower.includes('facebook')) return <Facebook className="h-5 w-5" />;
    if (platformLower.includes('github')) return <Github className="h-5 w-5" />;
    return <LinkIcon className="h-5 w-5" />;
  };

  // Format date display
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  // Will card component for grid view (Instagram style)
  const WillGridCard = ({ will }: { will: Will }) => {
    // Format budget for display with currency symbol
    const formattedBudget = will.budget 
      ? new Intl.NumberFormat('en-US', { 
          style: 'currency', 
          currency: will.currency || 'USD'
        }).format(will.budget) 
      : null;
    
    // Helper to render HTML content for description fallback
    const renderHTML = (html: string) => {
      return { __html: html };
    };

    // Process the media to find visual content (images or videos)
    const getMediaContent = () => {
      // Handle case where media might be null or undefined
      if (!will.media) return null;
      
      // Check if there are any attachments (non-visual media)
      let hasAttachments = false;
      
      // Handle array of media objects (with type/mimeType/url properties)
      if (Array.isArray(will.media)) {
        // Find the first image or video in the array
        for (const item of will.media) {
          // Case 1: Simple URL string (assume it's an image)
          if (typeof item === 'string') {
            return { mediaUrl: item, isVideo: false, hasAttachments: false };
          }
          
          // Case 2: Media object with url property
          if (item && typeof item === 'object') {
            // Skip if missing url
            if (!item.url) continue;
            
            // Determine if it's a video
            const isVideo = 
              item.type === 'video' || 
              (item.mimeType && item.mimeType.startsWith('video/'));
            
            // Determine if it's an image
            const isImage = 
              item.type === 'image' || 
              (item.mimeType && item.mimeType.startsWith('image/'));
            
            // If it's a visual media (image or video), return it
            if (isImage || isVideo) {
              return { mediaUrl: item.url, isVideo, hasAttachments: false };
            }
            
            // If it's not a visual media, mark that there are attachments
            hasAttachments = true;
          }
        }
      }
      
      // If we get here, there are no images or videos, but might have attachments
      return { mediaUrl: null, isVideo: false, hasAttachments };
    };
    
    const mediaContent = getMediaContent();
    const hasMediaContent = !!mediaContent?.mediaUrl;
    const hasAttachments = mediaContent?.hasAttachments || false;

    return (
      <div className="aspect-square bg-white overflow-hidden relative group cursor-pointer">
        {hasMediaContent ? (
          <div className="w-full h-full relative">
            <img 
              src={mediaContent.mediaUrl} 
              alt={will.title} 
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {mediaContent.isVideo && (
              <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                <div className="h-12 w-12 bg-white bg-opacity-80 rounded-full flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                  </svg>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Fallback to description text when no media, possibly with attachment icon
          <div className="w-full h-full flex items-center justify-center p-4 text-center bg-gradient-to-br from-blue-50 to-indigo-100 border border-gray-200 relative">
            <div 
              className="text-gray-700 font-medium overflow-hidden overflow-ellipsis line-clamp-6"
              dangerouslySetInnerHTML={renderHTML(will.description || will.title)}
            />
            
            {/* Show attachment icon if there are non-visual attachments */}
            {hasAttachments && (
              <div className="absolute top-2 right-2 bg-white bg-opacity-80 p-1.5 rounded-full">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                </svg>
              </div>
            )}
          </div>
        )}
        
        {/* Budget badge if available - matches InstagramWillCard */}
        {will.budget && (
          <div className="absolute bottom-2 left-2 text-sm font-bold text-black bg-yellow-200 px-2 py-1 rounded-md">
            {getCurrencySymbol(will.currency)}{will.budget}
          </div>
        )}
        
        {/* Overlay on hover with quick stats */}
        <div className="absolute inset-0 bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
          <div className="flex flex-col items-center p-4 w-full">
            <div className="font-semibold text-white text-sm mb-2 line-clamp-2 text-center max-w-[90%]">
              {will.title}
            </div>
            <div className="flex items-center gap-4 mb-3">
              <div className="flex items-center gap-1">
                <Heart className="h-4 w-4 text-white" />
                <span className="text-xs text-white">{will.likeCount || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <MessageCircle className="h-4 w-4 text-white" />
                <span className="text-xs text-white">{will.commentCount || 0}</span>
              </div>
            </div>
            <div className="mt-2 bg-white text-black text-xs px-3 py-1.5 rounded-full font-medium">
              View Want
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="container mx-auto px-4 pt-24">
          <div className="animate-pulse">Loading authentication...</div>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="container mx-auto px-4 pt-24">
          <div className="animate-pulse">Loading profile...</div>
        </main>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="container mx-auto px-4 pt-24">
          <div className="text-center">
            <p className="text-red-600">{profileError}</p>
            <Button onClick={() => fetchProfile()} className="mt-4">
              Retry
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="container mx-auto px-4 pt-24">
          <div className="text-center">
            <p className="text-gray-600">Profile not found</p>
            <Button onClick={() => navigate('/profile/edit')} className="mt-4">
              Create Profile
            </Button>
          </div>
        </main>
      </div>
    );
  }
  
  // If profile is private and viewer is not the owner, show restricted view
  if (!isProfileVisible() && !isProfileOwner()) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="container mx-auto px-4 pt-24">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-8 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Private Profile</h1>
            <p className="text-gray-600 mb-6">This profile is set to private by the user.</p>
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Go Back
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <main className="container mx-auto px-4 pt-24 pb-12 max-w-5xl">
        {/* Profile Header - Instagram Style */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="p-6">
            <div className="md:flex gap-6">
              {/* Avatar/Profile Image - Much smaller */}
              <div className="flex-shrink-0 flex justify-center md:justify-start mb-4 md:mb-0">
                <Avatar className="h-20 w-20 rounded-full border border-gray-200">
                  {(profile.profileImageUrl || profile.profilePictureUrl) ? (
                    <img 
                      src={getFullProfileImageUrl(profile.profileImageUrl || profile.profilePictureUrl)} 
                      alt={profile.username} 
                      className="h-full w-full object-cover rounded-full"
                    />
                  ) : (
                    <div className="bg-gradient-to-br from-purple-500 to-pink-600 h-full w-full flex items-center justify-center text-white text-xl font-bold">
                      {profile.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                </Avatar>
              </div>
              
              {/* Profile Info - Left column */}
              <div className="flex-1 md:pr-6 md:border-r md:border-gray-100">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-3">
                  <div className="flex flex-col md:flex-row items-center md:items-baseline gap-3">
                    <h1 className="text-xl font-medium text-gray-900 cursor-pointer hover:underline" onClick={() => profile?.userId && handleProfileClick(profile.userId)}>
                      {profile.username}
                    </h1>
                    <div className="flex gap-2">
                      {isProfileOwner() && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/profile/edit', { state: { profile } })}
                          className="rounded-md text-xs h-8"
            >
              Edit Profile
            </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-md h-8 w-8"
                      >
                        <Bell className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Stats - Instagram Style: Followers, Following, Posts */}
                {(isProfileOwner() || shouldShowContent('willHistory')) && (
                  <div className="flex gap-8 mb-3 justify-center md:justify-start">
                    <div className="text-center md:text-left">
                      <div className="font-semibold">{profile.postedWills?.length || 0}</div>
                      <div className="text-sm text-gray-500">posts</div>
                    </div>
                  </div>
                )}
                
                {/* Bio */}
                <div className="mb-2">
                  <p className="text-gray-600 text-sm whitespace-pre-wrap">{profile.bio || ""}</p>
                </div>
                
                {/* Contact & Location */}
                {shouldShowContent('contactInfo') && profile.contactInfo && (
                  <div className="flex items-center gap-2 text-gray-600 mb-2 text-sm">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span>{profile.contactInfo}</span>
                  </div>
                )}
              </div>
              
              {/* Talents & Expertise - Right column */}
              <div className="mt-4 md:mt-0 md:w-2/5 flex-shrink-0">
                <div className="border-t md:border-t-0 pt-4 md:pt-0 border-gray-100">
                  <h2 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                    <Sparkles className="h-4 w-4 text-indigo-500 mr-2" />
                    <span>Talents & Expertise</span>
                  </h2>
                  
                  {/* Skills - Superpowers */}
                  {profile.skills.length > 0 && (
                    <div className="mb-3">
                      <h3 className="text-xs font-medium text-purple-600 mb-1.5 flex items-center">
                        <Zap className="h-3.5 w-3.5 mr-1" />
                        <span>Superpowers</span>
                      </h3>
                      <div className="flex flex-wrap gap-1">
                        {profile.skills.map((skill) => {
                          const skillText = skill.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
                          return (
                            <div 
                              key={skill}
                              className="group relative"
                            >
                              <Badge 
                                variant="secondary"
                                className={`${getTagColor(skill)} hover:scale-105 transition-transform pl-2 pr-2.5 py-1 rounded-md font-medium text-xs cursor-default`}
                              >
                                {skillText}
                              </Badge>
                              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-2 py-1 rounded text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                {skillText}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Industries - Worlds */}
                  {profile.industries.length > 0 && (
                    <div>
                      <h3 className="text-xs font-medium text-blue-600 mb-1.5 flex items-center">
                        <Lightbulb className="h-3.5 w-3.5 mr-1" />
                        <span>Worlds</span>
                      </h3>
                      <div className="flex flex-wrap gap-1">
                        {profile.industries.map((industry) => {
                          const industryText = industry.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
                          return (
                            <div 
                              key={industry}
                              className="group relative"
                            >
                              <Badge 
                                variant="secondary"
                                className={`${getTagColor(industry)} hover:scale-105 transition-transform pl-2 pr-2.5 py-1 rounded-md font-medium text-xs cursor-default`}
                              >
                                {industryText}
                              </Badge>
                              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-2 py-1 rounded text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                {industryText}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Show message when no skills and industries are present */}
                  {profile.skills.length === 0 && profile.industries.length === 0 && (
                    <div className="text-center py-2">
                      <p className="text-gray-400 text-xs">No talents or expertise listed</p>
                      {isProfileOwner() && (
                        <Button 
                          variant="link" 
                          onClick={() => navigate('/profile/edit')}
                          className="text-indigo-500 text-xs mt-1 h-auto p-0"
                        >
                          Add your skills and industries
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Social Links Bar */}
          {profile.socialLinks && profile.socialLinks.length > 0 && (
            <div className="border-t border-gray-100 px-6 py-3 bg-gray-50">
              <div className="flex gap-4 overflow-x-auto">
                {profile.socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                  >
                    {renderSocialIcon(link.platform)}
                    <span className="text-sm">{link.platform}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Only show tabs if user has permission to view will history or reviews */}
        {(isProfileOwner() || shouldShowContent('willHistory') || shouldShowContent('reviews')) && (
          <div className="bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
            <Tabs defaultValue="postedWills" className="w-full" onValueChange={setActiveTab}>
              <div className="border-b border-gray-200">
                <TabsList className="h-full bg-transparent w-full flex justify-center rounded-none">
                  {/* Only show will tabs if should show will history */}
                  {(isProfileOwner() || shouldShowContent('willHistory')) && (
                    <>
                      <TabsTrigger 
                        value="postedWills" 
                        className="data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none data-[state=active]:rounded-none data-[state=active]:bg-transparent text-xs uppercase font-medium py-3 px-6"
                      >
                        <div className="flex items-center gap-2">
                          <Grid className="h-4 w-4" />
                          <span>Wants</span>
                        </div>
                      </TabsTrigger>
                      {/* <TabsTrigger 
                        value="providedWills" 
                        className="data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none data-[state=active]:rounded-none data-[state=active]:bg-transparent text-xs uppercase font-medium py-3 px-6"
                      >
                        <div className="flex items-center gap-2">
                          <Bookmark className="h-4 w-4" />
                          <span>Provided</span>
                        </div>
                      </TabsTrigger> */}
                    </>
                  )}
                  
                  {/* Only show reviews tab if should show reviews */}
                  {(isProfileOwner() || shouldShowContent('reviews')) && (
                    <TabsTrigger 
                      value="reviews" 
                      className="data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none data-[state=active]:rounded-none data-[state=active]:bg-transparent text-xs uppercase font-medium py-3 px-6"
                    >
                      <div className="flex items-center gap-2">
                        <MessageCircle className="h-4 w-4" />
                        <span>Reviews</span>
                      </div>
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>
              
              {/* Posted Wills Tab - Only render if should show will history */}
              {(isProfileOwner() || shouldShowContent('willHistory')) && (
                <TabsContent value="postedWills" className="pt-0 pb-0">
                  {profile.postedWills && profile.postedWills.length > 0 ? (
                    <div className="grid grid-cols-3 gap-[1px]">
                      {profile.postedWills.map((will) => (
                        <Link 
                          to={`/will/${will.id}`} 
                          key={will.id} 
                          className="block"
                          state={{ 
                            will: {
                              ...will,
                              urgency: will.status || 'PENDING',
                              reward: will.budget ? `${getCurrencySymbol(will.currency)}${will.budget}` : null
                            } 
                          }}
                        >
                          <WillGridCard will={will} />
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <p className="text-gray-600">No posted wants yet</p>
                    </div>
                  )}
                </TabsContent>
              )}
              
              {/* Provided Wills Tab - Only render if should show will history */}
              {(isProfileOwner() || shouldShowContent('willHistory')) && (
                <TabsContent value="providedWills" className="pt-0 pb-0">
                  {profile.providedWills && profile.providedWills.length > 0 ? (
                    <div className="grid grid-cols-3 gap-[1px]">
                      {profile.providedWills.map((will) => (
                        <Link 
                          to={`/will/${will.id}`} 
                          key={will.id} 
                          className="block"
                          state={{ 
                            will: {
                              ...will,
                              urgency: will.status || 'PENDING',
                              reward: will.budget ? `${getCurrencySymbol(will.currency)}${will.budget}` : null
                            } 
                          }}
                        >
                          <WillGridCard will={will} />
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <p className="text-gray-600">No provided wants yet</p>
                    </div>
                  )}
                </TabsContent>
              )}
              
              {/* Reviews Tab - Only render if should show reviews */}
              {(isProfileOwner() || shouldShowContent('reviews')) && (
                <TabsContent value="reviews" className="pt-0 pb-0">
                  {profile.reviews && profile.reviews.length > 0 ? (
                    <div className="p-4 space-y-4">
                      {profile.reviews.map((review) => (
                        <div key={review.id} className="bg-white rounded-lg border border-gray-200 p-4">
                          <div className="flex items-center gap-3 mb-2">
                            <Avatar className="h-8 w-8">
                              <div className="bg-gradient-to-br from-gray-500 to-gray-600 h-full w-full flex items-center justify-center text-white text-xs font-bold">
                                {review.reviewerName.charAt(0).toUpperCase()}
                              </div>
                            </Avatar>
                            <div>
                              <Link to={`/profile/${review.reviewerId}`} className="hover:underline">
                                <div className="font-medium text-sm">{review.reviewerName}</div>
                              </Link>
                              <div className="text-gray-500 text-xs">{formatDate(review.createdAt)}</div>
                            </div>
                            <div className="ml-auto flex">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <svg
                                  key={i}
                                  className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                          </div>
                          <p className="text-gray-600 text-sm">{review.comment}</p>
                          <div className="mt-2 text-xs text-gray-500">
                            <div className="font-medium">Will: {review.willTitle}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <p className="text-gray-600">No reviews yet</p>
                    </div>
                  )}
                </TabsContent>
              )}
            </Tabs>
        </div>
        )}
      </main>
    </div>
  );
};

export default ProfilePage;
